import type { FC } from 'react';
import { Button } from '@accure/ui';
import { ChevronRight } from 'lucide-react';

import { Card } from '@/components/ui/card';
import type { Alert } from '@/entities/alert';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useMe } from '@/hooks/use-me';

import { WarrantyDetailsContext } from '../../pages/warranty-details';
import { GetPenaltyOverview } from '../../queries/get-penalty-overview';
import { PenaltyChart } from '../penalty-overview/penalty-overview';

import { ChartCard } from './chart-card';
import { lookerStyle } from './looker-style';
import { PreviewLayout } from './preview-layout';
import { TableSuggestedActions } from './table-suggested-actions';
import { WarrantyDetailsLink } from './warranty-details-link';

export type WarrantyPenaltyPreviewProps = {
  alert: Alert;
};

export const WarrantyPenaltyPreview: FC<WarrantyPenaltyPreviewProps> = ({
  alert,
}) => {
  const assetFullFrontendId = alert.systemFrontendId ?? '';
  const asset = useCurrentAsset();
  const me = useMe();
  const penaltyOverview = GetPenaltyOverview.useQuery({
    filters: {},
    assetFullFrontendId,
    assetId: asset?.asset?.id ?? '',
    customerId: me.config.id,
  }).data;

  const style = lookerStyle();

  if (!penaltyOverview?.length) {
    return (
      <div className="py-4 text-center">
        <p>No penalty case defined</p>
      </div>
    );
  }

  return (
    <PreviewLayout
      chart={
        <ChartCard hasChart={true}>
          <WarrantyDetailsContext.Provider
            value={{
              fullFrontendId: assetFullFrontendId,
              id: asset?.asset?.id ?? '',
            }}
          >
            <PenaltyChart filters={{ siteId: assetFullFrontendId }} />
          </WarrantyDetailsContext.Provider>
        </ChartCard>
      }
      table={
        <Card className="flex h-full flex-col items-center rounded-lg border border-gray-200 bg-white px-2 py-3 shadow-lg">
          <h2 className={style.headline()}>Suggested Actions</h2>
          <TableSuggestedActions alert={alert} />
        </Card>
      }
      link={
        <WarrantyDetailsLink alert={alert}>
          <Button size="lg" data-testid="alert-get-more-details-button">
            Get more details
            <ChevronRight />
          </Button>
        </WarrantyDetailsLink>
      }
    />
  );
};
